import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from '@azure/msal-react';
import { useContext, useEffect, useState } from 'react';
import {
    Button,
    Typography,
    Box,
    Snackbar,
    Alert,
    AlertColor,
    Tabs,
    Tab
} from '@mui/material';
import { DependencyInjectionContext } from '../../../contexts/dependency-injection-context';
import { WelcomeBoard as WelcomeBoardData, WelcomeBoardStaff, WelcomeBoardIceBreaker, WelcomeBoardService, WelcomeBoardLayout } from '../../../common/interfaces';
import { LoadingIndicator } from '../../../components/core/LoadingIndicator/LoadingIndicator';
import { ClientSelector } from '../../../components/core/ClientSelector/ClientSelector';
import { FileManager } from '../../../components/WelcomeBoard/FileManager';
import {
    GeneralSettingsTab,
    StaffSettingsTab,
    ContentSettingsTab,
    AppointmentsSettingsTab,
    LayoutSettingsTab,
    AdvancedSettingsTab
} from '../../../components/WelcomeBoard/Settings';
import './WelcomeBoardSettings.scss';

export const WelcomeBoardSettings = () => {
    const { instance } = useMsal();
    const { formsApi, applicationState } = useContext(DependencyInjectionContext);
    
    // Loading states
    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);

    // Form state
    const [facebookUrl, setFacebookUrl] = useState<string>('');
    const [instagramUrl, setInstagramUrl] = useState<string>('');
    const [reviewUrl, setReviewUrl] = useState<string>('');
    const [cssOverride, setCssOverride] = useState<string>('');
    const [logoImage, setLogoImage] = useState<string>('');
    const [backgroundImage, setBackgroundImage] = useState<string>('');
    const [practiceName, setPracticeName] = useState<string>('');
    const [staff, setStaff] = useState<WelcomeBoardStaff[]>([]);
    const [iceBreakers, setIceBreakers] = useState<WelcomeBoardIceBreaker[]>([]);
    const [hygieneServiceTypes, setHygieneServiceTypes] = useState<string[]>([]);
    const [services, setServices] = useState<WelcomeBoardService[]>([]);
    const [mainLayoutId, setMainLayoutId] = useState<string>('');
    const [emptyLayoutId, setEmptyLayoutId] = useState<string>('');

    // Layout selection state
    const [layouts, setLayouts] = useState<WelcomeBoardLayout[]>([]);
    const [loadingLayouts, setLoadingLayouts] = useState<boolean>(false);

    // Alert state
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertText, setAlertText] = useState<string>('');
    const [alertType, setAlertType] = useState<AlertColor>('success');

    // Client selection state
    const [selectedClientId, setSelectedClientId] = useState<string>();

    // File manager state
    const [fileManagerOpen, setFileManagerOpen] = useState<boolean>(false);
    const [fileManagerTarget, setFileManagerTarget] = useState<'logo' | 'background' | 'staff'>('logo');
    const [staffImageIndex, setStaffImageIndex] = useState<number>(-1);

    // Tab state
    const [currentTab, setCurrentTab] = useState<number>(0);

    const handleCloseAlert = () => {
        setAlertOpen(false);
    };

    const showAlert = (message: string, type: AlertColor = 'success') => {
        setAlertText(message);
        setAlertType(type);
        setAlertOpen(true);
    };

    const loadLayouts = async () => {
        setLoadingLayouts(true);
        try {
            const data = await formsApi.get<WelcomeBoardLayout[]>(`GetWelcomeBoardLayouts?clientId=${applicationState.selectedClientId || ''}`);
            setLayouts(data || []);
        } catch (error) {
            console.error('Error loading layouts:', error);
            showAlert('Error loading layouts', 'error');
        } finally {
            setLoadingLayouts(false);
        }
    };

    const onClientSelected = async (clientId: string) => {
        setSelectedClientId(clientId);
        applicationState.selectedClientId = clientId;
        // Reload welcome board data for the selected client
        await loadWelcomeBoard();
        // Load layouts for the selected client
        await loadLayouts();
    };

    const loadWelcomeBoard = async () => {
        setLoading(true);
        try {
            const data = await formsApi.get<WelcomeBoardData>(`GetWelcomeBoard?clientId=${applicationState.selectedClientId || ''}`);
            if (data) {
                setFacebookUrl(data.facebookUrl || '');
                setInstagramUrl(data.instagramUrl || '');
                setReviewUrl(data.reviewUrl || '');
                setCssOverride(data.cssOverride || '');
                setLogoImage(data.logoImage || '');
                setBackgroundImage(data.backgroundImage || '');
                setPracticeName(data.practiceName || '');
                setStaff(data.staff || []);
                setIceBreakers(data.iceBreakers || []);
                setHygieneServiceTypes(data.hygieneServiceTypes || []);
                setServices(data.services || []);
                setMainLayoutId(data.mainLayoutId || '');
                setEmptyLayoutId(data.emptyLayoutId || '');
            }
        } catch (error) {
            console.error('Error loading welcome board:', error);
            showAlert('Error loading welcome board configuration', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const welcomeBoardData: WelcomeBoardData = {
                id: null, // Will be set by the API based on existing record
                clientId: selectedClientId || applicationState.selectedClientId || '',
                facebookUrl,
                instagramUrl,
                reviewUrl,
                cssOverride,
                logoImage,
                backgroundImage,
                practiceName,
                staff,
                iceBreakers,
                hygieneServiceTypes,
                services,
                mainLayoutId,
                emptyLayoutId
            };

            await formsApi.post<any, WelcomeBoardData>('/UpsertWelcomeBoard', {
                welcomeBoard: welcomeBoardData,
                clientId: selectedClientId || applicationState.selectedClientId
            });

            showAlert('Welcome board configuration saved successfully!', 'success');
        } catch (error) {
            console.error('Error saving welcome board:', error);
            showAlert('Error saving welcome board configuration', 'error');
        } finally {
            setSaving(false);
        }
    };

    const addStaffMember = () => {
        setStaff([...staff, { staffId: '', staffName: '' }]);
    };

    const removeStaffMember = (index: number) => {
        setStaff(staff.filter((_, i) => i !== index));
    };

    const updateStaffMember = (index: number, field: keyof WelcomeBoardStaff, value: string) => {
        const updatedStaff = [...staff];
        updatedStaff[index] = { ...updatedStaff[index], [field]: value };
        setStaff(updatedStaff);
    };

    const addIceBreaker = () => {
        setIceBreakers([...iceBreakers, { title: '', body: '' }]);
    };

    const removeIceBreaker = (index: number) => {
        setIceBreakers(iceBreakers.filter((_, i) => i !== index));
    };

    const updateIceBreaker = (index: number, field: keyof WelcomeBoardIceBreaker, value: string) => {
        const updatedIceBreakers = [...iceBreakers];
        updatedIceBreakers[index] = { ...updatedIceBreakers[index], [field]: value };
        setIceBreakers(updatedIceBreakers);
    };

    const addHygieneServiceType = () => {
        setHygieneServiceTypes([...hygieneServiceTypes, '']);
    };

    const removeHygieneServiceType = (index: number) => {
        setHygieneServiceTypes(hygieneServiceTypes.filter((_, i) => i !== index));
    };

    const updateHygieneServiceType = (index: number, value: string) => {
        const updated = [...hygieneServiceTypes];
        updated[index] = value;
        setHygieneServiceTypes(updated);
    };

    const openFileManager = (target: 'logo' | 'background' | 'staff', staffIndex: number = -1) => {
        setFileManagerTarget(target);
        setStaffImageIndex(staffIndex);
        setFileManagerOpen(true);
    };

    const handleFileSelect = (filePath: string) => {
        switch (fileManagerTarget) {
            case 'logo':
                setLogoImage(filePath);
                break;
            case 'background':
                setBackgroundImage(filePath);
                break;
            case 'staff':
                if (staffImageIndex >= 0) {
                    const updatedStaff = [...staff];
                    updatedStaff[staffImageIndex] = {
                        ...updatedStaff[staffImageIndex],
                        portraitImage: filePath
                    };
                    setStaff(updatedStaff);
                }
                break;
        }
        setFileManagerOpen(false);
    };

    useEffect(() => {
        if (applicationState.selectedClientId) {
            loadLayouts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicationState.selectedClientId]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    return (
        <div className="welcome-board page">
            <AuthenticatedTemplate>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" component="h1" gutterBottom>
                        Welcome Board Configuration
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Configure your welcome board settings including social media links, staff information, and ice breaker questions.
                    </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <ClientSelector onClientSelected={onClientSelected} />
                </Box>

                {loading ? (
                    <LoadingIndicator loading={loading} />
                ) : (
                    <Box>
                        {/* Tabs Navigation */}
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                            <Tabs value={currentTab} onChange={handleTabChange} aria-label="welcome board settings tabs">
                                <Tab label="General" />
                                <Tab label="Staff" />
                                <Tab label="Content" />
                                <Tab label="Appointments" />
                                <Tab label="Layout" />
                                <Tab label="Advanced" />
                            </Tabs>
                        </Box>

                        {/* Tab Panels */}
                        {currentTab === 0 && (
                            <GeneralSettingsTab
                                facebookUrl={facebookUrl}
                                instagramUrl={instagramUrl}
                                reviewUrl={reviewUrl}
                                practiceName={practiceName}
                                logoImage={logoImage}
                                backgroundImage={backgroundImage}
                                onFacebookUrlChange={setFacebookUrl}
                                onInstagramUrlChange={setInstagramUrl}
                                onReviewUrlChange={setReviewUrl}
                                onPracticeNameChange={setPracticeName}
                                onLogoImageChange={setLogoImage}
                                onBackgroundImageChange={setBackgroundImage}
                                onOpenFileManager={openFileManager}
                            />
                        )}

                        {currentTab === 1 && (
                            <StaffSettingsTab
                                staff={staff}
                                onAddStaff={addStaffMember}
                                onRemoveStaff={removeStaffMember}
                                onUpdateStaff={updateStaffMember}
                                onOpenFileManager={(index) => openFileManager('staff', index)}
                                clientId={selectedClientId || applicationState.selectedClientId || ''}
                            />
                        )}

                        {currentTab === 2 && (
                            <ContentSettingsTab
                                iceBreakers={iceBreakers}
                                services={services}
                                onAddIceBreaker={addIceBreaker}
                                onRemoveIceBreaker={removeIceBreaker}
                                onUpdateIceBreaker={updateIceBreaker}
                                onServicesChange={setServices}
                                clientId={selectedClientId || applicationState.selectedClientId || ''}
                            />
                        )}

                        {currentTab === 3 && (
                            <AppointmentsSettingsTab
                                hygieneServiceTypes={hygieneServiceTypes}
                                onAddServiceType={addHygieneServiceType}
                                onRemoveServiceType={removeHygieneServiceType}
                                onUpdateServiceType={updateHygieneServiceType}
                            />
                        )}

                        {currentTab === 4 && (
                            <LayoutSettingsTab
                                mainLayoutId={mainLayoutId}
                                emptyLayoutId={emptyLayoutId}
                                layouts={layouts}
                                loadingLayouts={loadingLayouts}
                                onMainLayoutChange={setMainLayoutId}
                                onEmptyLayoutChange={setEmptyLayoutId}
                            />
                        )}

                        {currentTab === 5 && (
                            <AdvancedSettingsTab
                                cssOverride={cssOverride}
                                onCssOverrideChange={setCssOverride}
                            />
                        )}

                        {/* Save Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleSave}
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Save Configuration'}
                            </Button>
                        </Box>
                    </Box>
                )}

                <Snackbar
                    open={alertOpen}
                    autoHideDuration={6000}
                    onClose={handleCloseAlert}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseAlert} severity={alertType} sx={{ width: '100%' }}>
                        {alertText}
                    </Alert>
                </Snackbar>

                <LoadingIndicator loading={saving} />

                {/* File Manager */}
                <FileManager
                    open={fileManagerOpen}
                    onClose={() => setFileManagerOpen(false)}
                    clientId={selectedClientId || applicationState.selectedClientId || ''}
                    onFileSelect={handleFileSelect}
                />
            </AuthenticatedTemplate>

            <UnauthenticatedTemplate>
                <p><a href="#" onClick={() => instance.loginRedirect()}>Sign In</a> to access Welcome Board!</p>
            </UnauthenticatedTemplate>
        </div>
    );
};